import * as React from "react";
import {
  gradePassword,
  InitialStateMinimumRequirements_PasswordForm,
} from "../logic";

export interface PasswordChecklistProps {
  initialState: InitialStateMinimumRequirements_PasswordForm;

  className?: string;

  name: string;
  form: { [key: string]: any };

  isPasswordFocused?: boolean;

  required?: boolean;
}

type PasswordPresentationChecklistItem = {
  valid: boolean;
  label: string;
};

type PasswordPresentationChecklist = PasswordPresentationChecklistItem[];

export const PasswordChecklist: React.FunctionComponent<
  PasswordChecklistProps
> = (props) => {
  const [unofficiallyShowing, setUnofficiallyShowing] = React.useState(false);
  const [show, setShow] = React.useState(false);

  const invalidChecklist = React.useMemo(() => {
    const value = props.form[props.name] || "";
    const grade = gradePassword({
      passwordValue: value,
      initialState: props.initialState,
    });

    const invalidChecklist: PasswordPresentationChecklist = [
      {
        valid: grade.hasMinimumCharacterLength,
        label: `At least ${props.initialState.password_form.minimum_length} characters`,
      },
      {
        valid: grade.hasLowerCase && grade.hasUpperCase,
        label: `Upper case and lower case letters`,
      },
      {
        valid: grade.hasDigits || grade.hasSpecialCharacters,
        label: `At least one number or symbol`,
      },
    ];

    return invalidChecklist;
  }, [props.name, props.form, props.isPasswordFocused, props.initialState]);

  const areThereAnyInvalidFields = React.useMemo(() => {
    return invalidChecklist.some(({ valid }) => !valid);
  }, [
    props.name,
    props.form,
    props.isPasswordFocused,
    props.initialState,
    invalidChecklist,
  ]);

  const checklist = React.useMemo(() => {
    let outputChecklist: PasswordPresentationChecklist = [];
    if (!areThereAnyInvalidFields) {
      outputChecklist = [
        {
          valid: true,
          label: "Your password is strong.",
        },
      ];
    } else {
      outputChecklist = invalidChecklist;
    }

    return (
      <ul>
        {outputChecklist.map((item) => {
          return (
            <li
              className={`password-checklist-component ${
                item.valid ? "valid" : "invalid"
              }`}
            >
              <p>{item.label}</p>
            </li>
          );
        })}
      </ul>
    );
  }, [
    props.name,
    props.form,
    props.isPasswordFocused,
    props.initialState,
    invalidChecklist,
    areThereAnyInvalidFields,
  ]);

  React.useEffect(() => {
    let thisAnimationTimeout: number | null = null;
    if (props.isPasswordFocused) {
      setShow(true);
      thisAnimationTimeout = window.setTimeout(() => {
        setUnofficiallyShowing(true);
      }, 16);
    } else {
      if (areThereAnyInvalidFields) {
        setUnofficiallyShowing(false);
        thisAnimationTimeout = window.setTimeout(() => {
          setShow(false);
        }, 300);
      } /* if (!areThereAnyInvalidFields) */ else {
        setShow(true);
        thisAnimationTimeout = window.setTimeout(() => {
          setUnofficiallyShowing(true);
        }, 16);
      }
    }
    return () => {
      if (thisAnimationTimeout) {
        window.clearTimeout(thisAnimationTimeout);
      }
    };
  }, [
    props.name,
    props.form,
    props.isPasswordFocused,
    props.initialState,
    invalidChecklist,
    areThereAnyInvalidFields,
  ]);

  if (!show) {
    return null;
  }

  return (
    <div
      className={`password-checklist ${unofficiallyShowing ? "show" : "hide"}`}
    >
      {checklist}
    </div>
  );
};
